import React, { useEffect } from "react";
import { BadgeStatus, SaleOrder } from "@/types/general";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import GeneralSection from "./parts/GeneralSection";
import ReferencesSection from "./parts/ReferencesSection";
import AddressSection from "./parts/AddressSection";
import ProductSection from "./parts/ProductSection";
import dayjs from "dayjs";
import { LineGroupUnion, SaleOrdersFormTyping, saleOrdersScheme } from "./types";
import LanguageSection from "./parts/LanguageSection";
import ReservationsSection from "./parts/ReservationsSection";
import { cn, normalizePrice } from "@helpers/utils";
import { buildProductMapFromLines } from "@components/Dashboard/pages/Sales/Orders/components/ViewLayout/ViewReservationsSection";
import UIButton from "@reusables/UIButton";
import { ReactComponent as StartPickingSVG } from "@assets/icons/ic_start_picking.svg";
import BaseSwitch from "@reusables/BaseSwitch";
import { useTranslation } from "react-i18next";
import { useIsReservationsEnabled } from "@redux/features/companies/useIsReservationsEnabled";
import IntegrationsSection from "./parts/IntegrationsSection";
import OfferActivationModal from "@components/Dashboard/pages/Sales/Orders/components/modals/OfferActivationModal";
import OrderReference from "./parts/OrderReference";
import CommentSection from "./parts/CommentSection";

interface MutationLayoutProperties {
    saleOrder: SaleOrder.Extended | undefined;
    onSubmit: (args: SaleOrdersFormTyping & { saveAndPick: boolean }) => void;
    disabled?: boolean;
}

export default function MutationLayout(props: MutationLayoutProperties) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders" });

    // Reading reservations feature flag to keep section rendering aligned with company settings.
    const isReservationsEnabled = useIsReservationsEnabled();

    const form = useForm<SaleOrdersFormTyping>({
        resolver: zodResolver(saleOrdersScheme),
        defaultValues: {
            mode: "creation",
            status: BadgeStatus.Base.new,
            is_editable: true,
            is_billing_for_delivery: false,
            order_date: dayjs(),
            reference_number: {},
            their_reference_name: null,
            comment: undefined,
            lines: [],
            is_invoiced: false,
            deleted_lines: [],
        },
    });

    // Persisting save-and-pick preference per mutation mode (creation/editing).
    const [saveAndPick, setSaveAndPick] = React.useState(() => {
        const stored = localStorage.getItem(
            `saveAndPick.${props.saleOrder ? "editing" : "creation"}`
        );
        return stored ? JSON.parse(stored) : false;
    });
    // Controlling submit-time Offer -> New confirmation modal visibility.
    const [isOfferActivationModalOpen, setIsOfferActivationModalOpen] = React.useState(false);

    // Holding pending submit payload without triggering rerenders while modal is opening/closing.
    const pendingSubmitDataRef = React.useRef<
        (SaleOrdersFormTyping & { saveAndPick: boolean }) | null
    >(null);

    React.useEffect(() => {
        // Saving save-and-pick preference to localStorage for current mutation mode.
        localStorage.setItem(
            `saveAndPick.${props.saleOrder ? "editing" : "creation"}`,
            JSON.stringify(saveAndPick)
        );
    }, [saveAndPick]);

    useEffect(() => {
        if (props.saleOrder) {
            // Hydrating editing form with backend payload when existing sale order is present.
            form.setValue("mode", "editing");
            form.setValue("is_editable", props.saleOrder.is_editable);
            form.setValue(
                "status",
                props.saleOrder.status.name === BadgeStatus.Base.offer
                    ? BadgeStatus.Base.offer
                    : BadgeStatus.Base.new
            );

            form.setValue("customer", props.saleOrder.customer);
            form.setValue("order_date", props.saleOrder.order_date);
            form.setValue("preferred_delivery_date", props.saleOrder.preferred_delivery_date);
            form.setValue("our_reference", props.saleOrder.our_reference);
            form.setValue("their_reference", props.saleOrder.their_reference);
            form.setValue(
                "their_reference_name",
                props.saleOrder.their_reference ? null : props.saleOrder.their_reference_name ?? null
            );
            form.setValue("reference_number", props.saleOrder.reference_number ?? {});
            form.setValue("payment_terms", props.saleOrder.payment_terms);
            form.setValue("delivery_terms", props.saleOrder.delivery_terms);
            form.setValue("language", props.saleOrder.language);
            form.setValue("comment", props.saleOrder.comment);
            if (props.saleOrder.billing_address)
                form.setValue("billing_address", {
                    name: props.saleOrder.billing_address.name,
                    street: props.saleOrder.billing_address.street,
                    city: props.saleOrder.billing_address.city,
                    country: props.saleOrder.billing_address.country,
                });
            form.setValue("delivery_address", props.saleOrder?.delivery_address);

            form.setValue("is_billing_for_delivery", props.saleOrder.is_billing_for_delivery);

            if (props.saleOrder.project) {
                form.setValue("twenty_four_seven_office.project", props.saleOrder.project);
            }

            const combinedLines: LineGroupUnion[] = props.saleOrder.lines.map((line, index) => {
                if (line.type === "line") {
                    return {
                        ...line.data,
                        unit_price: normalizePrice(line.data.unit_price),
                        ...(line.data.alternative_name && {
                            alternative_name: line.data.alternative_name,
                        }),
                    };
                } else {
                    return {
                        id: line.data.id,
                        name: line.data.name,
                        comment: line.data.comment,
                        module: line.data.module,
                        module_entity_id: line.data.module_entity_id,
                        lines: line.data.lines.map((groupLine) => ({
                            ...groupLine.data,
                            unit_price: normalizePrice(groupLine.data.unit_price),
                        })),
                    };
                }
            });

            form.setValue("lines", combinedLines);

            form.setValue("deleted_lines", []);

            // Rebuilding reservations form shape from API reservation payload.
            if (props.saleOrder.reservations?.length > 0) {
                // Resolving product metadata from lines because reservation payload contains only product_id.
                const productMap = buildProductMapFromLines(props.saleOrder.lines);

                // Mapping API reservations (product_id) to RHF reservations (product object).
                const formReservations = props.saleOrder.reservations
                    .map((reservation) => {
                        const product = productMap.get(reservation.product_id);
                        if (!product) return null;

                        return {
                            product,
                            quantity: reservation.quantity,
                        };
                    })
                    .filter(
                        (
                            r
                        ): r is {
                            product: { id: number; name: string; code: string };
                            quantity: number;
                        } => r !== null
                    );

                form.setValue("reservations", formReservations);
            }

            // Setting reservations expiration date when backend is returning it.
            if (props.saleOrder.reservations_expiration_date) {
                form.setValue(
                    "reservations_expiration_date",
                    dayjs(props.saleOrder.reservations_expiration_date)
                );
            }
        }
    }, [props.saleOrder]);

    const onSubmit = form.handleSubmit((data: SaleOrdersFormTyping) => {
        // Unused, because we forbid editing SO if it's invoiced
        // if (props.saleOrder?.is_invoiced) {
        //     for (let i = 0; i < form.getValues("lines").length; i++) {
        //         const originalQuantity = props.saleOrder?.lines[i].data?.quantity;
        //         const newQuantity = form.getValues("lines")[i].data!.quantity;

        //         // If the new quantity is less than the original, show error and stop the mutation
        //         if (newQuantity < originalQuantity) {
        //             toast.error(`Quantity for product ${form.getValues("lines")[i].data?.product.name} cannot be less than the original quantity.`);
        //             return;
        //         }
        //     }
        // }

        const submitData = { ...data, saveAndPick };
        const shouldConfirmOfferActivationOnSave =
            props.saleOrder?.status.name === BadgeStatus.Base.offer &&
            data.mode === "editing" &&
            data.status === BadgeStatus.Base.new;

        // Intercepting Offer -> New transition and opening confirmation only during save action.
        if (shouldConfirmOfferActivationOnSave) {
            pendingSubmitDataRef.current = submitData;
            setIsOfferActivationModalOpen(true);
            return;
        }

        props.onSubmit(submitData);
    }, console.error);

    return (
        <FormProvider {...form}>
            <OrderReference />
            <div className={cn("space-y-[32px]", props.disabled && "hidden")}>
                <GeneralSection />
                <ReferencesSection />
                <AddressSection />
                <LanguageSection />
                <IntegrationsSection />
                <CommentSection />
                <ProductSection />
                {/* Reservations section - only shown when feature is enabled */}
                {isReservationsEnabled && <ReservationsSection />}
                <SaveAndPickControl
                    saveAndPick={saveAndPick}
                    setSaveAndPick={setSaveAndPick}
                    label={t("general.saveAndPick.label")}
                />
                <div className="flex justify-center space-x-2">
                    <UIButton onClick={() => onSubmit()} size={"lg"} className="min-w-[200px]">
                        <span>{t("general.buttons.save")}</span>
                    </UIButton>
                </div>
                <OfferActivationModalWatcher
                    isOpen={isOfferActivationModalOpen}
                    onClose={() => {
                        pendingSubmitDataRef.current = null;
                        setIsOfferActivationModalOpen(false);
                    }}
                    onConfirm={() => {
                        const pendingSubmitData = pendingSubmitDataRef.current;
                        if (!pendingSubmitData) return;

                        props.onSubmit(pendingSubmitData);
                        pendingSubmitDataRef.current = null;
                        setIsOfferActivationModalOpen(false);
                    }}
                />
            </div>
        </FormProvider>
    );
}

interface OfferActivationModalWatcherProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

interface SaveAndPickControlProps {
    saveAndPick: boolean;
    setSaveAndPick: React.Dispatch<React.SetStateAction<boolean>>;
    label: string;
}

function SaveAndPickControl({ saveAndPick, setSaveAndPick, label }: SaveAndPickControlProps) {
    const { control } = useFormContext<SaleOrdersFormTyping>();
    const watchedStatus = useWatch({ control, name: "status" });
    const isOfferStatus = watchedStatus === BadgeStatus.Base.offer;

    // Keeping status-specific save-and-pick behavior isolated to this block only.
    useEffect(() => {
        if (isOfferStatus && saveAndPick) {
            setSaveAndPick(false);
        }
    }, [isOfferStatus, saveAndPick, setSaveAndPick]);

    return (
        <div
            className={cn(
                "flex items-center justify-between space-x-2 border border-solid border-gray-300 rounded-md p-2 mt-4 transition-[.150s] user-select-none",
                !isOfferStatus && "hover:bg-gray-300/10 cursor-pointer",
                isOfferStatus && "opacity-70 cursor-not-allowed"
            )}
            onClick={() => {
                if (!isOfferStatus) {
                    setSaveAndPick(!saveAndPick);
                }
            }}
        >
            <div
                className={cn(
                    "flex items-center space-x-2",
                    saveAndPick && !isOfferStatus ? "text-accent" : "text-secondary"
                )}
            >
                <StartPickingSVG className="w-4 h-4 text-inherit" />
                <span className={cn("text-sm font-normal text-inherit")}>{label}</span>
            </div>
            <BaseSwitch
                checked={saveAndPick}
                aria-label={"Save and Pick switch"}
                disabled={isOfferStatus}
            />
        </div>
    );
}

function OfferActivationModalWatcher(props: OfferActivationModalWatcherProps) {
    const { control } = useFormContext<SaleOrdersFormTyping>();

    // Isolating modal field subscriptions so only this wrapper is rerendering on related changes.
    const customer = useWatch({ control, name: "customer" });
    const paymentTerms = useWatch({ control, name: "payment_terms" });
    const deliveryTerms = useWatch({ control, name: "delivery_terms" });

    return (
        <OfferActivationModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            onConfirm={props.onConfirm}
            customer={customer?.name}
            paymentTerms={paymentTerms?.name}
            deliveryTerms={deliveryTerms?.name}
        />
    );
}
